import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow, CTABand, Photo, Chips, CONCERN_CHIP } from "@/components/Shared";
import { getPost } from "@/data/content";
import { getConcern } from "@/data/concerns";
import { PHOTOS } from "@/data/photos";
import NotFound from "@/pages/NotFound";
import { NorwoodFigure } from "@/components/NorwoodScale";

const FIGURES = { norwood: NorwoodFigure };

const JournalPost = () => {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <NotFound />;

  const concernChips = (post.concerns || [])
    .map((path) => {
      const [family, cslug] = path.split("/");
      const c = getConcern(family, cslug);
      return c ? CONCERN_CHIP(path, c.title) : null;
    })
    .filter(Boolean);

  const midpoint = Math.ceil(post.body.length / 2);
  const Figure = FIGURES[post.figure];

  return (
    <>
      <SEO title={`${post.title} | Lumière Aesthetics`} description={post.excerpt} />

      <section className="bg-warm-white pt-36 pb-14" data-testid="journal-post-header">
        <div className="max-w-[68ch] mx-auto px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
            <Link to="/journal" data-testid="journal-back-link" className="eyebrow hover:text-sea-green transition-colors duration-300">
              ← Journal
            </Link>
            <Eyebrow className="mt-8">{post.category}</Eyebrow>
            <h1 className="text-deep-sea">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-6 text-soft-grey text-sm">
              <span data-testid="journal-byline">{post.byline}</span>
              <span className="w-1 h-1 rounded-full bg-gold" aria-hidden="true" />
              <span>{post.date}</span>
              <span className="w-1 h-1 rounded-full bg-gold" aria-hidden="true" />
              <span>{post.readTime}</span>
            </div>
            <div className="mt-10">
              <Photo photo={PHOTOS[post.photo]} className="aspect-[21/10]" width={1600} eager testId="journal-post-photo" />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-warm-white pb-24" data-testid="journal-post-body">
        <article className="max-w-[68ch] mx-auto px-8">
          <Reveal>
            {post.body.slice(0, midpoint).map((p, i) => (
              <div key={i}>
                <p className={`text-ink/85 leading-[1.85] text-[17px] mb-6 ${i === 0 ? "font-serif text-xl text-deep-sea leading-[1.6]" : ""}`}>
                  {p}
                </p>
                {Figure && i === 1 && <Figure />}
              </div>
            ))}
            <blockquote className="border-l-2 border-gold pl-8 my-12" data-testid="journal-pull-quote">
              <p className="font-serif italic font-light text-sea-green text-2xl leading-relaxed">{post.pullQuote}</p>
            </blockquote>
            {post.body.slice(midpoint).map((p, i) => (
              <p key={i} className="text-ink/85 leading-[1.85] text-[17px] mb-6">{p}</p>
            ))}
          </Reveal>

          {concernChips.length > 0 && (
            <Reveal className="mt-12">
              <Eyebrow className="mb-5">Related concerns</Eyebrow>
              <Chips items={concernChips} />
            </Reveal>
          )}

          <Reveal className="mt-14 pt-8 border-t border-hairline/50">
            <p className="text-soft-grey text-xs leading-relaxed">
              This article is general information, not medical advice. Any surgical or invasive procedure carries
              risks — before proceeding, you should seek a second opinion from an appropriately qualified health
              practitioner.
            </p>
          </Reveal>
        </article>
      </section>

      <CTABand />
    </>
  );
};

export default JournalPost;
